import { z } from 'zod';

/**
 * Badges next to a person's name ("blue tick" and friends). Staff define the types in the admin panel —
 * name in both languages, what it means, colour, icon and order — and give them to people, optionally
 * until a date. Each colour should keep one meaning, so people learn to read them.
 */

export const BADGE_ICONS = ['check', 'building', 'school', 'shield', 'star', 'sparkle', 'award', 'crown', 'heart', 'briefcase', 'bug', 'flask', 'code', 'rocket', 'trophy', 'languages', 'handshake', 'gem', 'lifebuoy', 'palette'] as const;
export type BadgeIcon = (typeof BADGE_ICONS)[number];

/** What everyone sees: the type's public face (the grant's note and dates stay with staff). */
export interface BadgeDto {
  slug: string;
  name: string;
  description: string;
  color: string;
  icon: BadgeIcon;
}

/** GET /users/badges?ids=… → each person's live badges, most important first. */
export type UserBadgesDto = Record<string, BadgeDto[]>;
export const MAX_BADGE_LOOKUP = 100;

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const badgeTypeInputSchema = z.object({
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,31}$/),
  nameTr: z.string().trim().min(1).max(40),
  nameEn: z.string().trim().min(1).max(40),
  descriptionTr: z.string().trim().max(200).default(''),
  descriptionEn: z.string().trim().max(200).default(''),
  color: hex,
  icon: z.enum(BADGE_ICONS),
  priority: z.number().int().min(0).max(1000).default(100),
  active: z.boolean().default(true),
});
export type BadgeTypeInput = z.input<typeof badgeTypeInputSchema>;
export const badgeTypeUpdateSchema = badgeTypeInputSchema.omit({ slug: true }).partial().strict();

export interface AdminBadgeTypeDto {
  id: string;
  slug: string;
  nameTr: string;
  nameEn: string;
  descriptionTr: string;
  descriptionEn: string;
  color: string;
  icon: BadgeIcon;
  priority: number;
  active: boolean;
  /** People holding it now (not revoked, not expired). */
  holders: number;
}

/** Several people at once (the panel's people picker); each gets the same badge, date and note. */
export const MAX_BADGE_GRANT_PEOPLE = 50;
export const badgeGrantSchema = z.object({
  /** @username or the user's id. */
  user: z.string().trim().min(1).max(64),
  typeId: z.uuid(),
  expiresAt: z.iso.datetime({ offset: true }).nullable().default(null),
  note: z.string().trim().max(500).default(''),
});
export const badgeGrantUpdateSchema = z.object({ expiresAt: z.iso.datetime({ offset: true }).nullable().optional(), note: z.string().trim().max(500).optional() }).strict();

export interface AdminBadgeGrantDto {
  id: string;
  typeId: string;
  user: { id: string; username: string; displayName: string };
  note: string;
  grantedAt: string;
  grantedBy: string | null;
  expiresAt: string | null;
  /** live | expired | revoked */
  state: 'live' | 'expired' | 'revoked';
  revokedAt: string | null;
}

export const badgeGrantManySchema = z.object({
  users: z.array(z.string().trim().min(1).max(64)).min(1).max(MAX_BADGE_GRANT_PEOPLE),
  typeId: z.uuid(),
  expiresAt: z.iso.datetime({ offset: true }).nullable().default(null),
  note: z.string().trim().max(500).default(''),
});

/** The panel's people picker: by name or username, with the badges each one already has. */
export interface BadgePersonDto {
  id: string;
  username: string;
  displayName: string;
  badgeTypeIds: string[];
}
